import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Image,
  Text,
  Title,
  Group,
  Button,
  Stack,
  Badge,
  Pagination,
  Select,
  SimpleGrid,
  Paper,
  AspectRatio,
  Grid,
} from '@mantine/core';

import { Loader } from '../../components/common/Loader';

import { Link } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import {
  IconCalendar,
  IconUser,
  IconSearch,
} from '@tabler/icons-react';

import { SectionTitle } from '../../components/common/SectionTitle';

import { useSEO } from '../../hooks/useSEO';

import { galleryService } from '../../services/galleryService';

const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://thalvora-exports.onrender.com';

const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500' viewBox='0 0 800 500'%3E%3Crect width='800' height='500' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

interface BlogPost {
  id: string;

  title: string;

  excerpt: string;

  image: string;

  author: string;

  date: string;

  category: string;

  slug: string;

  readTime: string;
}

// 🔥 Static fallback data
const staticBlogs: BlogPost[] = [
  {
    id: '1',

    title:
      'The Rising Demand for Indian Agricultural Products in Global Markets',

    excerpt:
      'Explore how Indian agricultural exports are gaining traction worldwide and what it means for international buyers.',

    image:
      '/assets/images/blog/agricultural-exports.jpg',

    author: 'John Doe',

    date: '2024-01-15',

    category: 'Market Insights',

    slug:
      'rising-demand-indian-agricultural-products',

    readTime: '5 min read',
  },
];

export const BlogPage: React.FC = () => {
  const [currentPage, setCurrentPage] =
    useState(1);

  const [selectedCategory, setSelectedCategory] =
    useState<string | null>(null);

  const [sortBy, setSortBy] = useState<
    string | null
  >('latest');

  const [blogs, setBlogs] =
    useState<BlogPost[]>(staticBlogs);

  const [loading, setLoading] = useState(true);

  const [useFallback, setUseFallback] =
    useState(false);

  const { seo, loading: seoLoading } =
    useSEO('blog');

  const postsPerPage = 6;

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        // 🔥 STEP 1: Load cache instantly
        const cachedBlogs = sessionStorage.getItem(
          'blog_page_data'
        );

        if (
          cachedBlogs &&
          cachedBlogs !== 'undefined'
        ) {
          const parsed = JSON.parse(cachedBlogs);

          setBlogs(parsed.blogs || []);

          setUseFallback(
            parsed.useFallback || false
          );

          setLoading(false);
        }

        // 🔥 STEP 2: Fetch latest blogs
        const gallery =
          await galleryService.getPublicGallery(
            'BLOG'
          );

        const items: BlogPost[] = gallery.items
          .filter(
            (item) =>
              item.type === 'IMAGE' &&
              item.active
          )

          .map((item) => {
            let meta: any = {};

            try {
              meta = JSON.parse(
                item.metadata || '{}'
              );
            } catch (e) {}

            if (!meta.isBlogPost) return null;

            return {
              id: item.id,

              title:
                meta.title || item.title,

              excerpt: meta.excerpt || '',

              image: item.imageUrl,

              author: meta.author || '',

              date: meta.date || '',

              category: meta.category || '',

              slug: meta.slug || '',

              readTime:
                meta.readTime || '',
            };
          })

          .filter(
            (item): item is BlogPost =>
              item !== null
          );

        let finalBlogs = staticBlogs;

        let fallbackState = false;

        if (items.length > 0) {
          finalBlogs = items;

          fallbackState = false;
        } else {
          finalBlogs = staticBlogs;

          fallbackState = true;
        }

        setBlogs(finalBlogs);

        setUseFallback(fallbackState);

        // 🔥 STEP 3: Save cache
        sessionStorage.setItem(
          'blog_page_data',
          JSON.stringify({
            blogs: finalBlogs,
            useFallback: fallbackState,
          })
        );
      } catch (err) {
        console.error(
          'Failed to fetch blog posts:',
          err
        );

        setBlogs(staticBlogs);

        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const categories = [
    'All',

    ...new Set(
      blogs.map((blog) => blog.category)
    ),
  ];

  const filteredBlogs =
    selectedCategory &&
    selectedCategory !== 'All'
      ? blogs.filter(
          (blog) =>
            blog.category === selectedCategory
        )
      : blogs;

  const sortedBlogs = [
    ...filteredBlogs,
  ].sort((a, b) => {
    if (sortBy === 'latest')
      return (
        new Date(b.date).getTime() -
        new Date(a.date).getTime()
      );

    if (sortBy === 'oldest')
      return (
        new Date(a.date).getTime() -
        new Date(b.date).getTime()
      );

    if (sortBy === 'title')
      return a.title.localeCompare(
        b.title
      );

    return 0;
  });

  const indexOfLastPost =
    currentPage * postsPerPage;

  const indexOfFirstPost =
    indexOfLastPost - postsPerPage;

  const currentPosts = sortedBlogs.slice(
    indexOfFirstPost,
    indexOfLastPost
  );

  const totalPages = Math.ceil(
    sortedBlogs.length / postsPerPage
  );

  const getImageUrl = (url: string) => {
    if (!url) return PLACEHOLDER_IMG;

    if (url.startsWith('http')) return url;

    if (url.startsWith('/assets')) return url;

    return `${API_URL}${
      url.startsWith('/') ? '' : '/'
    }${url}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';

    return new Date(
      dateString
    ).toLocaleDateString('en-US', {
      year: 'numeric',

      month: 'long',

      day: 'numeric',
    });
  };

  if (seoLoading || loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>

        <meta
          name="description"
          content={seo?.metaDescription}
        />

        <meta
          name="keywords"
          content={seo?.metaKeywords?.join(', ')}
        />

        <meta
          property="og:title"
          content={seo?.ogTitle}
        />

        <meta
          property="og:description"
          content={seo?.ogDescription}
        />

        {seo?.ogImage && (
          <meta
            property="og:image"
            content={seo.ogImage}
          />
        )}

        <meta
          name="twitter:title"
          content={seo?.twitterTitle}
        />

        <meta
          name="twitter:description"
          content={seo?.twitterDescription}
        />

        {seo?.twitterImage && (
          <meta
            name="twitter:image"
            content={seo.twitterImage}
          />
        )}

        {seo?.canonicalUrl && (
          <link
            rel="canonical"
            href={seo.canonicalUrl}
          />
        )}
      </Helmet>

      <Container size="xl">
        <SectionTitle
          title="Our Blog"
          subtitle="Latest insights, news, and guides from the world of agricultural exports"
        />

        {useFallback && (
          <Text
            size="sm"
            c="orange"
            mb="md"
          >
            ℹ️ Using fallback data. Add
            blog posts via admin panel to
            customise.
          </Text>
        )}

        {/* FILTERS */}
        <Paper
          withBorder
          p="md"
          radius="lg"
          mb={30}
        >
          <Grid>
            <Grid.Col
              span={{ base: 12, md: 6 }}
            >
              <Select
                placeholder="Filter by category"
                data={categories.map((cat) => ({
                  value: cat,
                  label: cat,
                }))}
                value={selectedCategory}
                onChange={
                  setSelectedCategory
                }
                clearable
                leftSection={
                  <IconSearch size={16} />
                }
              />
            </Grid.Col>

            <Grid.Col
              span={{ base: 12, md: 6 }}
            >
              <Select
                placeholder="Sort by"
                data={[
                  {
                    value: 'latest',
                    label: 'Latest First',
                  },

                  {
                    value: 'oldest',
                    label: 'Oldest First',
                  },

                  {
                    value: 'title',
                    label: 'Title A-Z',
                  },
                ]}
                value={sortBy}
                onChange={setSortBy}
              />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* BLOG GRID */}
        <SimpleGrid
          cols={{ base: 1, md: 2, lg: 3 }}
          spacing="lg"
        >
          {currentPosts.map((blog) => (
            <Card
              key={blog.id}
              shadow="sm"
              padding="lg"
              radius="lg"
              withBorder
              className="hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
            >
              <Card.Section>
                <AspectRatio
                  ratio={16 / 10}
                >
                  <Image
                    src={getImageUrl(
                      blog.image
                    )}
                    height={200}
                    alt={blog.title}
                    fit="cover"
                    fallbackSrc={
                      PLACEHOLDER_IMG
                    }
                    loading="lazy"
                  />
                </AspectRatio>
              </Card.Section>

              <Stack gap="md" mt="md">
                <Group justify="space-between">
                  <Badge
                    color="primary"
                    variant="light"
                  >
                    {blog.category}
                  </Badge>

                  <Group gap="xs">
                    <IconCalendar
                      size={14}
                    />

                    <Text
                      size="xs"
                      c="dimmed"
                    >
                      {formatDate(
                        blog.date
                      )}
                    </Text>
                  </Group>
                </Group>

                <Title
                  order={3}
                  size="h4"
                  lineClamp={2}
                >
                  {blog.title}
                </Title>

                <Text
                  size="sm"
                  c="dimmed"
                  lineClamp={3}
                >
                  {blog.excerpt}
                </Text>

                <Group
                  justify="space-between"
                  align="center"
                >
                  <Group gap="xs">
                    <IconUser
                      size={14}
                    />

                    <Text
                      size="xs"
                      c="dimmed"
                    >
                      {blog.author}
                    </Text>
                  </Group>

                  <Text
                    size="xs"
                    c="dimmed"
                  >
                    {blog.readTime}
                  </Text>
                </Group>

                <Button
                  component={Link}
                  to={`/blog/${blog.slug}`}
                  variant="light"
                  color="primary"
                  fullWidth
                >
                  Read Article
                </Button>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <Group
            justify="center"
            mt={40}
          >
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              color="primary"
              size="lg"
              radius="md"
            />
          </Group>
        )}
      </Container>
    </>
  );
};